import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Avatar, Box, Button, CircularProgress, IconButton, Paper, Stack, Tab, Tabs,
  Typography, Chip, Select, MenuItem,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  OpenInNew as OpenInNewIcon,
  ChatBubbleOutline as CommentIcon,
  CalendarMonth as CalendarIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { contactsApi, UpdateContactPayload } from '../lib/api/contactsApi'
import { customersApi } from '../lib/api/customersApi'
import { dealsApi } from '../lib/api/dealsApi'
import { leadsApi } from '../lib/api/leadsApi'
import { usersAdminApi } from '../lib/api/usersAdminApi'
import { themeColors } from '../app/layout/theme'
import { callLogsApi, CallLog, CallStatus, SaveOutcomePayload } from '../lib/api/callLogsApi'
import { callStore } from '../lib/stores/callStore'
import { CallHistorySectionGeneric } from '../components/calls/CallHistorySectionGeneric'
import { UniversalActivityTimeline } from '../components/shared/UniversalActivityTimeline'
import { ContactActivity } from '../lib/api/contactsApi'
import { observer } from 'mobx-react-lite'
import { CommentItem, CommentInput, CommentsEmptyState } from '../components/shared/NotesSection'
import { contactNotesApi } from '../lib/api/contactNotesApi'
import { maskPhoneNumber } from '../utils/phoneUtils'
import AttachmentsSection from '../components/shared/AttachmentsSection'
import { SideCard } from '../components/shared/SideCard'
import { DescriptionSection } from '../components/shared/DescriptionSection'
import { InfoRow, SummaryRow } from '../components/shared/DetailViewWrappers'
import { EntityDetailTabs } from '../components/shared/EntityDetailTabs'
import { emailApi } from '../lib/api/emailApi'
import { EmailHistorySection } from '../components/shared/EmailHistorySection'
import { SendEmailDrawer, SendEmailData } from '../components/drawers/SendEmailDrawer'
import { authStore } from '../lib/stores/authStore'
import { FormInput } from '../components/ui'
import { usePermission } from '../lib/hooks/usePermission'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

// ── Call Flow Hook removed (using global callStore)


// ── Main Page ─────────────────────────────────────────────────────────────────

const ContactDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  // ── UI state
  const [activeTab, setActiveTab] = useState(0) // Activity by default

  const { hasAction } = usePermission()
  const canEdit = hasAction('/contacts', 'update')
  const [emailOpen, setEmailOpen] = useState(false)

  // Global calling store usage

  // ── Queries
  const { data: contact, isLoading, isError } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => contactsApi.getById(id!),
    enabled: !!id
  })

  const { data: activities = [], isLoading: loadingActs } = useQuery<ContactActivity[]>({
    queryKey: ['contact-activities', id],
    queryFn: () => contactsApi.getActivity(id!),
    enabled: !!id
  })

  const { data: dealsData, isLoading: loadingDeals } = useQuery({
    queryKey: ['contact-deals', id],
    queryFn: () => dealsApi.getAll({ contactId: id, pageSize: 1 }),
    enabled: !!id && activeTab === 2,
  })

  const { data: leadsData, isLoading: loadingLeads } = useQuery({
    queryKey: ['contact-leads', id, contact?.email, contact?.phone],
    queryFn: () => leadsApi.getAll({
      search: contact?.email || contact?.phone || contact?.name || undefined,
      statuses: ['ALL'],
      pageSize: 5
    }),
    enabled: !!contact && activeTab === 1
  })

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }),
    staleTime: 60_000
  })

  const { data: notes = [], isLoading: loadingNotes } = useQuery({
    queryKey: ['contact-notes', id],
    queryFn: () => contactNotesApi.getAll(id!),
    enabled: !!id
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['contact', id] })
    qc.invalidateQueries({ queryKey: ['contact-activities', id] })
    qc.invalidateQueries({ queryKey: ['contact-notes', id] })
    qc.invalidateQueries({ queryKey: ['emails', 'CONTACT', contact?.id] })
    qc.invalidateQueries({ queryKey: ['contact-deals', id] })
    qc.invalidateQueries({ queryKey: ['contact-leads', id] })
  }

  // ── Mutations
  const updateMut = useMutation({
    mutationFn: (p: UpdateContactPayload) => contactsApi.update(id!, p),
    onSuccess: () => {
      toast.success('Contact updated')
      invalidate()
    },
    onError: () => toast.error('Failed to update')
  })

  const sendEmailMutation = useMutation({
    mutationFn: (data: SendEmailData) =>
      emailApi.send({
        entityType: 'CONTACT',
        entityId: contact?.id,
        to: data.to,
        cc: data.cc.length > 0 ? data.cc : undefined,
        bcc: data.bcc.length > 0 ? data.bcc : undefined,
        subject: data.subject,
        htmlBody: data.body,
        files: data.attachments.length > 0 ? data.attachments : undefined,
      }),
    onSuccess: () => {
      toast.success('Email sent successfully')
      setEmailOpen(false)
      invalidate()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Failed to send email')
    },
  })


  const assignMut = useMutation({
    mutationFn: (aid: string) => customersApi.assign(contact!.customerId, aid),
    onSuccess: () => { toast.success('Reassigned'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Reassignment failed (API may not be wired)')
  })

  // ── Notes Mutations ──
  const addNoteMut = useMutation({
    mutationFn: (content: string) => contactNotesApi.create(id!, content),
    onSuccess: () => { invalidate(); toast.success('Note added') }
  })
  const updateNoteMut = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) => contactNotesApi.update(id!, noteId, content),
    onSuccess: () => { invalidate(); toast.success('Note updated') }
  })
  const deleteNoteMut = useMutation({
    mutationFn: (noteId: string) => contactNotesApi.delete(id!, noteId),
    onSuccess: () => { invalidate(); toast.success('Note deleted') }
  })

  if (isLoading) return <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
  if (isError || !contact) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="error">Contact not found.</Typography>
      <Button onClick={() => navigate('/contacts')} sx={{ mt: 2 }} startIcon={<BackIcon />}>Back</Button>
    </Box>
  )

  const color = avatarColor(contact.name)
  const TABS = ['Activity', 'Leads', 'Deals', 'Call', 'Email', 'Comments', 'Attachments']

  const contactTabs = [
    {
      label: 'Activity',
      content: <UniversalActivityTimeline logs={activities} isLoading={loadingActs} entityType="contact" />,
    },
    {
      label: 'Leads',
      content: (
        <Box>
          {loadingLeads ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            <Stack spacing={1.5}>
              {(leadsData?.items ?? []).length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb' }}>
                  <Typography fontSize="14px" color="text.secondary">No leads found for this contact.</Typography>
                </Box>
              ) : (
                (leadsData?.items ?? []).map(lead => (
                  <Paper key={lead.id} elevation={0} sx={{
                    p: 2, borderRadius: '12px', border: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: themeColors.primary, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                  }}>
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                        <Typography fontSize="13px" color="text.secondary">Lead Name:</Typography>
                        <Typography fontSize="13px" fontWeight={700}
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          sx={{ color: themeColors.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {lead.name}
                        </Typography>
                        <IconButton size="small" onClick={() => navigate(`/leads/${lead.id}`)} sx={{ color: themeColors.primary, p: 0.5 }}>
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                      {lead.companyName && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                          <Typography fontSize="13px" color="text.secondary">Company:</Typography>
                          <Typography fontSize="13px" fontWeight={700}>{lead.companyName}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography fontSize="13px" color="text.secondary">Owner:</Typography>
                        <Typography fontSize="13px" fontWeight={700}>{lead.owner?.name || '—'}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip label={lead.status} size="small" sx={{
                        height: 20, fontSize: '10px', fontWeight: 700, mb: 1,
                        bgcolor: lead.status === 'CONVERTED' ? '#f0fdf4' : lead.status === 'JUNK' ? '#fef2f2' : '#eff6ff',
                        color: lead.status === 'CONVERTED' ? '#16a34a' : lead.status === 'JUNK' ? '#dc2626' : '#1d4ed8'
                      }} />
                      <Typography fontSize="12px" color="text.secondary">
                        Source: <Typography component="span" fontSize="12px" fontWeight={700} color="text.primary">{lead.source}</Typography>
                      </Typography>
                    </Box>
                  </Paper>
                ))
              )}
            </Stack>
          )}
        </Box>
      ),
    },
    {
      label: 'Deals',
      content: (
        <Box>
          {loadingDeals ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            <Stack spacing={1.5}>
              {!(dealsData?.items && dealsData.items.length > 0) ? (
                <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb' }}>
                  <Typography fontSize="14px" color="text.secondary">No deals found for this contact.</Typography>
                </Box>
              ) : (
                (() => {
                  const deal = dealsData.items[0];
                  return (
                    <Paper key={deal.id} elevation={0} sx={{
                      p: 2, borderRadius: '12px', border: '1px solid #e5e7eb',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: themeColors.primary, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                    }}>
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                          <Typography fontSize="13px" color="text.secondary">Deal Name:</Typography>
                          <Typography fontSize="13px" fontWeight={700}
                            onClick={() => navigate(`/deals/${deal.id}`)}
                            sx={{ color: themeColors.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {deal.title}
                          </Typography>
                          <IconButton size="small" onClick={() => navigate(`/deals/${deal.id}`)} sx={{ color: themeColors.primary, p: 0.5 }}>
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                          <Typography fontSize="13px" color="text.secondary">Company Name:</Typography>
                          <Typography fontSize="13px" fontWeight={700}>{deal.customer?.companyName || '—'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography fontSize="13px" color="text.secondary">Owner:</Typography>
                          <Typography fontSize="13px" fontWeight={700}>{deal.owner.name}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip label={deal.status} size="small" sx={{
                          height: 20, fontSize: '10px', fontWeight: 700, mb: 1,
                          bgcolor: deal.status === 'WON' ? '#f0fdf4' : deal.status === 'SALES' ? '#f5f3ff' : deal.status === 'LOST' ? '#fef2f2' : '#eff6ff',
                          color: deal.status === 'WON' ? '#16a34a' : deal.status === 'SALES' ? '#7c3aed' : deal.status === 'LOST' ? '#dc2626' : '#2563eb'
                        }} />
                        <Typography fontSize="12px" color="text.secondary">
                          Amount: <Typography component="span" fontSize="12px" fontWeight={700} color="text.primary">
                            {deal.currency} {deal.amount?.toLocaleString()}
                          </Typography>
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })()
              )}
            </Stack>
          )}
        </Box>
      ),
    },
    {
      label: 'Call',
      content: (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<PhoneIcon />}
              onClick={() => callStore.showPlaceCall({
                entityType: 'CONTACT',
                entityId: contact.id,
                name: contact.name,
                phone: contact.phone,
                companyName: contact.customer?.companyName ?? contact.customer?.name ?? undefined
              })}
              disabled={callStore.isStarting}
              sx={{ borderRadius: '8px', textTransform: 'none', background: themeColors.gradient.blue }}
            >
              {callStore.isStarting ? <CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} /> : 'New Call'}
            </Button>
          </Box>
          <CallHistorySectionGeneric
            entityId={contact.id}
            entityType="contact"
            emptyHint="Use the New Call button to initiate a call with this contact"
          />
        </Box>
      ),
    },
    {
      label: 'Email',
      content: (
        <EmailHistorySection
          entityType="CONTACT"
          entityId={contact.id}
          canModify={true}
          onCompose={() => {
            if (!contact.email) { toast.error('No email address found for this contact'); return }
            setEmailOpen(true)
          }}
        />
      ),
    },
    {
      label: 'Comments',
      content: (
        <Box>
          <CommentInput onAdd={async (c) => { await addNoteMut.mutateAsync(c) }} isAdding={addNoteMut.isPending} />
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : notes.length === 0 ? (
            <CommentsEmptyState />
          ) : (
            <Stack spacing={0}>
              {notes.map((note, index) => (
                <CommentItem
                  key={note.id}
                  note={note}
                  onUpdate={async (c) => { await updateNoteMut.mutateAsync({ noteId: note.id, content: c }) }}
                  onDelete={() => deleteNoteMut.mutate(note.id)}
                  isUpdating={updateNoteMut.isPending}
                  isLast={index === notes.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      ),
    },
    {
      label: 'Attachments',
      content: <AttachmentsSection moduleType="contact" recordId={contact.id} canModify={true} />,
    },
  ]

  const roleName = authStore.user?.roleName ?? ''
  const isSuperAdmin = roleName === 'SUPER_ADMIN'

  return (
    <Box sx={{
      height: '100%', overflowY: 'auto', bgcolor: '#f5f6fa',
      '&::-webkit-scrollbar': { width: '5px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: '4px' }
    }}>

      {/* ── Breadcrumb ── */}
      <Box sx={{ px: 3, pt: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => navigate('/contacts')} sx={{ color: '#6b7280', mr: 0.5 }}>
          <BackIcon fontSize="small" />
        </IconButton>
        <Typography fontSize="13px" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
          onClick={() => navigate('/contacts')}>
          Contact
        </Typography>
        <Typography fontSize="13px" color="text.secondary" sx={{ mx: 0.25 }}>/</Typography>
        <Typography fontSize="13px" fontWeight={600} color="text.primary">{contact.name}</Typography>
      </Box>

      {/* ── Hero Card ── */}
      <Box sx={{ px: 3, pt: 1.5, pb: 0 }}>
        <Paper elevation={0} sx={{
          border: `2px solid ${themeColors.primary}45`,
          borderRadius: '14px', p: 2.5, bgcolor: '#fff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>

            {/* Avatar */}
            <Avatar sx={{
              width: 62, height: 62, bgcolor: color, color: '#fff',
              fontSize: '22px', fontWeight: 700, flexShrink: 0,
              boxShadow: `0 4px 12px ${color}50`
            }}>
              {getInitials(contact.name)}
            </Avatar>

            {/* Name + meta */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700} fontSize="20px" color="text.primary" mb={0.5}>{contact.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                  <Typography fontSize="13px" color="text.secondary">{contact.role || 'Designation'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CalendarIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                  <Typography fontSize="13px" color="text.secondary">Contact since {formatDate(contact.createdAt)}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button size="small" variant="outlined" startIcon={<PhoneIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => callStore.showPlaceCall({
                  entityType: 'CONTACT',
                  entityId: contact.id,
                  name: contact.name,
                  phone: contact.phone,
                  companyName: contact.customer?.companyName ?? contact.customer?.name ?? undefined
                })}
                disabled={callStore.isStarting}
                sx={{
                  borderRadius: '8px', textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#374151',
                  borderColor: '#d1d5db', px: 2, py: 0.75, '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                }}>
                {callStore.isStarting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : 'Call'}
              </Button>
              <Button size="small" variant="outlined" startIcon={<EmailIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => {
                  if (!contact.email) { toast.error('No email address found for this contact'); return }
                  setEmailOpen(true)
                }}
                sx={{
                  borderRadius: '8px', textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#374151',
                  borderColor: '#d1d5db', px: 2, py: 0.75, '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                }}>
                Email
              </Button>
              <Button size="small" variant="outlined" startIcon={<CommentIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => setActiveTab(5)}
                sx={{
                  borderRadius: '8px', textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#374151',
                  borderColor: '#d1d5db', px: 2, py: 0.75, '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }
                }}>
                Comment
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ display: 'flex', gap: 2.5, p: 3, pt: 2, alignItems: 'flex-start' }}>

        {/* ════ Sidebar ════ */}
        <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Basic Info */}
          <SideCard title="Basic Info">
            <Stack spacing={0.5}>
              <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="Phone" value={<Typography fontSize="13px">{maskPhoneNumber(contact.phone)}</Typography>} />
              <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />} label="Email" value={<Typography fontSize="13px" color={contact.email ? 'text.primary' : 'text.disabled'}>{contact.email || 'No email'}</Typography>} />
              <InfoRow icon={<BusinessIcon sx={{ fontSize: 16 }} />} label="Company Name"
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography
                      fontSize="13px"
                      noWrap
                      onClick={() => navigate(`/customers/${contact.customerId}`)}
                      sx={{
                        maxWidth: 160,
                        cursor: 'pointer',
                        '&:hover': { color: themeColors.primary, textDecoration: 'underline' }
                      }}
                    >
                      {contact.customer.companyName || contact.customer.name}
                    </Typography>
                    <IconButton size="small" onClick={() => navigate(`/customers/${contact.customerId}`)}
                      sx={{ bgcolor: themeColors.primary, color: '#fff', '&:hover': { bgcolor: themeColors.primaryDark }, width: 22, height: 22, flexShrink: 0 }}>
                      <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                }
              />
            </Stack>
          </SideCard>

          <DescriptionSection
            value={contact.description}
            onSave={(val) => updateMut.mutate({ description: val })}
            isPending={updateMut.isPending}
            canEdit={canEdit}
          />

          {/* Business / LOB */}
          <SideCard>
            <SummaryRow label="Business" value={contact.orgName || contact.customer?.orgName || '—'} />
            <SummaryRow label="LOB" value={contact.lobName || '—'} />
          </SideCard>


        </Box>

        {/* ════ Main Content ════ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EntityDetailTabs
            tabs={contactTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            padding={2.5}
            tabHeight={48}
            tabFontSize="13px"
            tabFontWeight={600}
            tabPaddingX={2.5}
            borderRadius="12px"
          />
        </Box>

      </Box>

      <SendEmailDrawer
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        defaultTo={contact.email ? [contact.email] : []}
        onSend={(data) => sendEmailMutation.mutate(data)}
        isSending={sendEmailMutation.isPending}
      />


      {/* Global calling UI overlay (rendered in AppLayout) */}
    </Box>
  )
})

export default ContactDetailPage
